/**
 * The rows of Probe's sidebar, drawn the way T3's thread list draws a
 * thread: one line, the outcome at the start, the day at the end, the
 * selected row filled (`resolveThreadRowClassName`), and the arrow keys moving
 * between rows.
 *
 * @module components/apps/probe/sidebar/ProbeSidebarParts
 */
import type { ProbeOutcome } from "@amoreg/contracts";
import { Link, useLocation } from "@tanstack/react-router";
import { CircleDashedIcon, CircleIcon, ListIcon, RotateCwIcon } from "lucide-react";
import { useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { practiceAreas, weakAreaCount } from "@amoreg/shared/probePractice";
import { resolveThreadRowClassName } from "../../../Sidebar.logic";
import { Skeleton } from "../../../ui/skeleton";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "../../../ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../../ui/tooltip";
import type { AppAreaNavProps } from "../../AppsSidebar";
import { AppAreaNavRow } from "../../appAreaNavParts";
import { useProbeLiveStatus } from "../ProbeAreaProvider";
import { useProbeInterviews, type ProbeInterviewsStatus } from "../probeInterviewsStore";
import { probeSectionFromPathname, type ProbeSection } from "../probePaths";
import { OutcomeGlyph } from "../ProbeParts";
import {
  liveElapsed,
  probeSidebarView,
  type ProbeSidebarInterview,
  type ProbeSidebarLive,
  type ProbeSidebarView,
} from "./ProbeSidebar.logic";

/** The interviews a sidebar lists; the rest are on Home. */
export const PROBE_SIDEBAR_LIMIT = 12;

/** The clock, ticking every `intervalMs` while `running`. */
function useNow(intervalMs: number, running: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs, running]);
  return now;
}

export interface ProbeSidebarState {
  readonly section: ProbeSection | null;
  /** Home is showing every interview: All interviews is the selected row, not Home. */
  readonly allInterviews: boolean;
  readonly view: ProbeSidebarView;
  readonly status: ProbeInterviewsStatus;
  readonly reload: () => void;
  /** Practice areas that need work, for Home's count. */
  readonly weak: number;
  readonly live: boolean;
}

export function useProbeSidebar({
  organizationId,
  pathname,
}: Pick<AppAreaNavProps, "organizationId" | "pathname">): ProbeSidebarState {
  const section = probeSectionFromPathname(pathname);
  const search: { readonly show?: unknown } = useLocation({ select: (location) => location.search });
  const allInterviews = section?.kind === "practice" && search.show === "interviews";
  const live = useProbeLiveStatus((state) => state.live);
  const startedAt = useProbeLiveStatus((state) => state.startedAt);
  const { status, interviews, reload } = useProbeInterviews(organizationId);
  const weak = useMemo(() => weakAreaCount(practiceAreas(interviews)), [interviews]);
  // Ages move on by the minute; the call's own clock ticks in its row.
  const nowMs = useNow(60_000, true);
  const view = useMemo(
    () =>
      probeSidebarView({
        interviews,
        live: live ? { startedAt } : null,
        section,
        nowMs,
        limit: PROBE_SIDEBAR_LIMIT,
      }),
    [interviews, live, startedAt, section, nowMs],
  );
  return { section, allInterviews, view, status, reload, weak, live };
}

const ROW_SELECTOR = "[data-probe-sidebar-row]";

/** Up and Down move between the list's rows, Home and End to its ends, as T3's thread list does. */
export function onListKeyDown(event: KeyboardEvent<HTMLElement>): void {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  const rows = [...event.currentTarget.querySelectorAll<HTMLElement>(ROW_SELECTOR)];
  if (rows.length === 0) return;
  const at = rows.indexOf(document.activeElement as HTMLElement);
  const next =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? rows.length - 1
        : event.key === "ArrowDown"
          ? Math.min(rows.length - 1, at + 1)
          : Math.max(0, at === -1 ? rows.length - 1 : at - 1);
  event.preventDefault();
  rows[next]?.focus();
}

/** The interview's outcome at the start of its row: the debrief's glyph, an empty ring without one. */
function RowOutcome({ outcome }: { readonly outcome: ProbeOutcome | null }) {
  return (
    <span className="flex w-4 shrink-0 justify-center">
      {outcome === null ? (
        <CircleIcon aria-hidden className="size-3.5 text-sidebar-muted-foreground/45" />
      ) : (
        <OutcomeGlyph outcome={outcome} />
      )}
    </span>
  );
}

/** The call that is up, pinned above the saved ones, with its running time. */
export function LiveRow({
  live,
  orgSegment,
  onNavigate,
}: {
  readonly live: ProbeSidebarLive;
  readonly orgSegment: string;
  readonly onNavigate: () => void;
}) {
  const now = useNow(1000, live.startedAt !== null);
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={live.active}
        className={cn(
          resolveThreadRowClassName({ isActive: live.active, isSelected: false }),
          "gap-2.5 px-2.5 text-sidebar-foreground",
        )}
        data-probe-sidebar-row=""
        data-probe-live=""
        render={<Link to="/$org/apps/probe" params={{ org: orgSegment }} onClick={onNavigate} />}
      >
        <span aria-hidden className="flex w-4 shrink-0 justify-center">
          {/* T3 Code's running-thread glyph, still: the timer beside it says the call is going. */}
          <CircleDashedIcon className="size-3.5 text-sidebar-foreground" />
        </span>
        <span className="min-w-0 flex-1 truncate">Live interview</span>
        {live.startedAt !== null ? (
          <span className="shrink-0 text-[10px] text-sidebar-foreground tabular-nums">
            {liveElapsed(live.startedAt, now)}
          </span>
        ) : null}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/** One saved interview on one line: outcome, name, and its day on the right. */
export function InterviewRow({
  interview,
  orgSegment,
  onNavigate,
}: {
  readonly interview: ProbeSidebarInterview;
  readonly orgSegment: string;
  readonly onNavigate: () => void;
}) {
  return (
    <SidebarMenuItem>
      <Tooltip>
        <TooltipTrigger
          delay={600}
          render={
            <SidebarMenuButton
              isActive={interview.active}
              className={cn(
                resolveThreadRowClassName({ isActive: interview.active, isSelected: false }),
                "gap-2.5 px-2.5",
              )}
              aria-label={[interview.title, interview.day, interview.detail]
                .filter(Boolean)
                .join(", ")}
              data-probe-sidebar-row=""
              data-app-area-row={`interview:${interview.id}`}
              render={
                <Link
                  to="/$org/apps/probe/interviews/$sessionId"
                  params={{ org: orgSegment, sessionId: interview.id }}
                  onClick={onNavigate}
                />
              }
            />
          }
        >
          <RowOutcome outcome={interview.outcome} />
          <span className="min-w-0 flex-1 truncate">{interview.title}</span>
          <span
            className={cn(
              "shrink-0 text-[10px] tabular-nums",
              interview.active ? "text-sidebar-foreground" : "text-secondary-label",
            )}
          >
            {interview.day}
          </span>
        </TooltipTrigger>
        <TooltipPopup side="right" className="max-w-72 whitespace-normal leading-snug">
          <span className="block">{interview.title}</span>
          {interview.detail ? (
            <span className="block text-muted-foreground">{interview.detail}</span>
          ) : null}
        </TooltipPopup>
      </Tooltip>
    </SidebarMenuItem>
  );
}

/** While the list loads, fails or is empty: the line that stands in for it. */
export function InterviewListPlaceholder({
  status,
  empty,
  reload,
}: {
  readonly status: ProbeInterviewsStatus;
  readonly empty: boolean;
  readonly reload: () => void;
}): ReactNode {
  if (status === "loading") {
    return Array.from({ length: 4 }, (_, index) => (
      <SidebarMenuItem key={index} aria-hidden>
        <div className="flex h-8 items-center gap-2.5 px-2.5">
          <Skeleton className="size-1.5 rounded-full" />
          <Skeleton className="h-3 flex-1" />
          <Skeleton className="h-2.5 w-5" />
        </div>
      </SidebarMenuItem>
    ));
  }
  if (status === "error") {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          className="h-8 gap-2 px-2 text-sidebar-muted-foreground/70"
          onClick={reload}
          data-app-area-row="interviews-retry"
        >
          <RotateCwIcon />
          <span className="truncate">Couldn&rsquo;t load &middot; Retry</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }
  if (empty) {
    return (
      <p className="px-2 py-1.5 text-xs text-sidebar-muted-foreground/70">No interviews yet</p>
    );
  }
  return null;
}

/** Home's every interview (`?show=interviews`), with how many there are. */
export function AllInterviewsRow({
  total,
  active,
  orgSegment,
  onNavigate,
}: {
  readonly total: number;
  readonly active: boolean;
  readonly orgSegment: string;
  readonly onNavigate: () => void;
}) {
  return (
    <AppAreaNavRow
      data-app-area-row="interviews"
      active={active}
      muted
      icon={<ListIcon />}
      label="All interviews"
      count={total}
      render={
        <Link
          to="/$org/apps/probe/practice"
          params={{ org: orgSegment }}
          search={{ show: "interviews" }}
          onClick={onNavigate}
        />
      }
    />
  );
}

/** The list: the arrow keys move between its rows. */
export function InterviewList({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <SidebarMenu
      aria-label={label}
      className="ps-px"
      data-probe-sidebar-list=""
      onKeyDown={onListKeyDown}
    >
      {children}
    </SidebarMenu>
  );
}
